// =============================================================================
// ProviderMappingsTab — UPT Mapping CRUD table + modal
// =============================================================================

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  Button,
  Badge,
  Modal,
  ModalFooter,
  Input,
} from '@tkpod/ui';
import { api } from '@/lib/api';
import { ProviderVariantMappingsSection } from './ProviderVariantMappingsSection';
import { UptMappingPicker } from '@/components/catalog/UptMappingPicker';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Mapping {
  id: string;
  uptId: string;
  uptName: string;
  uptType: string;
  brand: string | null;
  modelName: string | null;
  externalSku: string | null;
  externalProductId: string | null;
  costOverride: string | null;
  capabilities: Record<string, unknown> | null;
  priority: number | null;
  isDefault: boolean;
  isActive: boolean;
  mappedVariants: number;
  totalVariants: number;
  createdAt: string;
}

interface ProviderMappingsTabProps {
  providerId: string;
  mappings: Mapping[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProviderMappingsTab({ providerId, mappings }: ProviderMappingsTabProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [selectedUptId, setSelectedUptId] = useState('');
  const [externalSku, setExternalSku] = useState('');
  const [externalProductId, setExternalProductId] = useState('');
  const [costOverride, setCostOverride] = useState('');
  const [priority, setPriority] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Mapping | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'provider', providerId] });

  const resetAndClose = () => {
    setShowModal(false);
    setSelectedUptId('');
    setExternalSku('');
    setExternalProductId('');
    setCostOverride('');
    setPriority('');
    setIsDefault(false);
  };

  const existingUptIds = new Set(mappings.map((m) => m.uptId));

  // --- Mutations --------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      api.post(`/api/v1/admin/providers/${providerId}/mappings`, data),
    onSuccess: () => { invalidate(); resetAndClose(); },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ mappingId, data }: { mappingId: string; data: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/providers/${providerId}/mappings/${mappingId}`, data),
    onSuccess: () => { invalidate(); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (mappingId: string) =>
      api.delete(`/api/v1/admin/providers/${providerId}/mappings/${mappingId}`),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedUptId) return;

    createMutation.mutate({
      uptId: selectedUptId,
      externalSku: externalSku || undefined,
      externalProductId: externalProductId || undefined,
      costOverride: costOverride ? Number(costOverride) : undefined,
      priority: priority ? Number(priority) : undefined,
      isDefault: isDefault || undefined,
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-text-primary">
          UPT Mappings ({mappings.length})
        </h4>
        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
          Add Mapping
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        {mappings.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
            No UPT mappings yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">UPT</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Brand / Model</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Mapped Variants</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">External SKU</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Cost Override</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Default</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface">
              {mappings.map((m) => (
                <tr key={m.id} className="transition-colors hover:bg-surface-secondary">
                  <td className="px-4 py-3 text-sm font-medium text-text-primary whitespace-nowrap">
                    <button
                      className="text-brand-600 hover:underline text-left"
                      onClick={() => navigate(`/catalog/${m.uptId}`)}
                    >
                      {m.uptName}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {m.brand || m.modelName ? (
                      <span>
                        {m.brand && <span className="font-medium text-text-primary">{m.brand}</span>}
                        {m.brand && m.modelName && ' / '}
                        {m.modelName && <span>{m.modelName}</span>}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Badge variant="info" size="sm">{m.uptType}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {m.priority ?? '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {m.totalVariants > 0 ? (
                      <span className={`text-sm font-medium ${m.mappedVariants === m.totalVariants ? 'text-emerald-600' : m.mappedVariants > 0 ? 'text-amber-600' : 'text-text-tertiary'}`}>
                        {m.mappedVariants} / {m.totalVariants}
                      </span>
                    ) : (
                      <span className="text-sm text-text-tertiary">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {m.externalSku ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {m.costOverride ? `$${m.costOverride}` : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {m.isDefault ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-600">
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Default
                      </span>
                    ) : (
                      <button
                        className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600 transition-colors hover:bg-amber-500/20 hover:border-amber-400/60 disabled:opacity-50"
                        disabled={updateMutation.isPending}
                        onClick={() => updateMutation.mutate({ mappingId: m.id, data: { isDefault: true } })}
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        Set Default
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="disabled:opacity-50"
                      disabled={updateMutation.isPending}
                      onClick={() => updateMutation.mutate({ mappingId: m.id, data: { isActive: !m.isActive } })}
                    >
                      <Badge variant={m.isActive ? 'success' : 'danger'} size="sm" dot>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right space-x-2">
                    <button
                      className="text-xs text-brand-600 hover:underline"
                      onClick={() => navigate(`/catalog/${m.uptId}`)}
                    >
                      Manage in Catalog →
                    </button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteTarget(m)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Variant Mappings per UPT */}
      {mappings.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-semibold text-text-primary">
            Variant Mappings
          </h4>
          {mappings.map((m) => (
            <ProviderVariantMappingsSection
              key={m.uptId}
              providerId={providerId}
              uptId={m.uptId}
              uptName={m.uptName}
            />
          ))}
        </div>
      )}

      {/* Add Mapping Modal */}
      <Modal
        open={showModal}
        onClose={resetAndClose}
        title="Add UPT Mapping"
        description="Map a Universal Product Template to this provider."
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <UptMappingPicker
            value={selectedUptId}
            onChange={setSelectedUptId}
            excludeIds={existingUptIds}
            enabled={showModal}
          />
          <Input
            label="External SKU"
            value={externalSku}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setExternalSku(e.target.value)}
            hint="Provider-side SKU (optional)"
          />
          <Input
            label="External Product ID"
            value={externalProductId}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setExternalProductId(e.target.value)}
            hint="Provider-side product identifier (optional)"
          />
          <Input
            label="Cost Override ($)"
            type="number"
            value={costOverride}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCostOverride(e.target.value)}
            hint="Override the default cost for this mapping (optional)"
          />
          <Input
            label="Priority"
            type="number"
            min={1}
            value={priority}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPriority(e.target.value)}
            hint="Lower number = higher priority (optional)"
          />
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="rounded border-border"
            />
            Default provider for this UPT
          </label>

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
              {createMutation.error?.message ?? 'Failed to create mapping.'}
            </div>
          )}

          <ModalFooter>
            <Button variant="outline" type="button" onClick={resetAndClose}>Cancel</Button>
            <Button
              variant="primary"
              type="submit"
              loading={createMutation.isPending}
              disabled={!selectedUptId}
            >
              Add Mapping
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Remove Mapping"
        description={
          deleteTarget
            ? `Are you sure you want to remove the mapping for "${deleteTarget.uptName}"? This action cannot be undone.`
            : undefined
        }
        size="sm"
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => {
              if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
            }}
            loading={deleteMutation.isPending}
          >
            Remove Mapping
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
